<script setup>
import { computed, onUnmounted, ref, onMounted, watch } from 'vue';
import { getUnixTime } from 'date-fns';
import { useRoute, useRouter } from 'vue-router';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection.js';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import wootConstants from 'dashboard/constants/globals';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import AceleresOwnershipTransferModal from './AceleresOwnershipTransferModal.vue';
import CustomSnoozeModal from 'dashboard/components/CustomSnoozeModal.vue';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SNOOZE_NOTIFICATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

const props = defineProps({
  activeNotification: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const transferCopy = {
  backAria: 'Voltar',
  title: 'Transferir conversa',
  subtitle: 'Escolha o novo responsável',
  searchPlaceholder: 'Buscar usuário',
  availableGroup: 'Disponíveis',
  availableStatus: 'Disponível',
  offlineGroup: 'Offline',
  offlineStatus: 'Offline',
  emptyState: 'Nenhum usuário encontrado',
};

const ensureMovementReasonPreferenceScript = () => {
  if (typeof window.__aclGetMovementReasonPreference === 'function') {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    const existingScript = document.querySelector(
      'script[data-aceleres-movement-reason-preference]'
    );
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src =
      '/aceleres-funnel-movement-reason-preference.js?v=20260803-funnel-regression-v1';
    script.dataset.aceleresMovementReasonPreference = 'true';
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', resolve, { once: true });
    document.head.appendChild(script);
  });
};

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showTransferMenu, toggleTransferMenu] = useToggle(false);
const showCustomSnoozeModal = ref(false);
const showOwnershipModal = ref(false);
const ownershipStatus = ref(null);
const ownershipStatusLoading = ref(false);
const ownershipSubmitting = ref(false);
let ownershipStatusPollId = null;

// Guarda anti-clickaway: o clique que abre o sub-menu "Transferir" faz o Vue
// desmontar o DropdownMenu; o v-on-clickaway avalia no tick seguinte, não
// encontra mais o botão clicado dentro do container e dispara closeAll(),
// fechando tudo no mesmo evento. closeAll ignora esse único ciclo. Ver closeAll.
let transferJustOpened = false;

const currentChat = computed(() => store.getters.getSelectedChat);
const currentUser = computed(() => store.getters.getCurrentUser);
const notificationMeta = computed(() => store.getters['notifications/getMeta']);
const activeInboxNotification = computed(() => {
  if (props.activeNotification?.id) return props.activeNotification;
  if (route.name !== 'inbox_view_conversation') return null;

  const notificationsGetter =
    store.getters['notifications/getFilteredNotifications'];
  const records =
    typeof notificationsGetter === 'function'
      ? notificationsGetter({ sortOrder: 'desc' })
      : [];
  const conversationId = Number(route.params.id);

  return (
    records.find(
      notification => Number(notification.primary_actor?.id) === conversationId
    ) || null
  );
});
const isNotificationContext = computed(() =>
  Boolean(activeInboxNotification.value?.id)
);
const { isAdmin } = useAdmin();
const { enterSelectionMode } = useMessageSelection();
const isTrainingChat = computed(() =>
  isTrainingConversation(currentChat.value, currentUser.value)
);

// ─── Status helpers (resolve / reopen / pending / snoozed) ───
const isOpen = computed(
  () => currentChat.value?.status === wootConstants.STATUS_TYPE.OPEN
);
const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](currentChat.value?.inbox_id)
);
const isPersonalConversation = computed(() => {
  const labels = Array.isArray(currentChat.value?.labels)
    ? currentChat.value.labels
    : [];
  const attrs = currentChat.value?.additional_attributes || {};
  const inboxAttrs = currentInbox.value?.additional_attributes || {};

  return (
    labels.includes('pessoal') ||
    isAceleresIndividualOwn(inboxAttrs.connection_type) ||
    Number(attrs.personal_owner_chatwoot_user_id) > 0 ||
    Number(attrs.personal_origin_chatwoot_user_id) > 0 ||
    Number(attrs.owner_chatwoot_user_id) > 0
  );
});
const isResolved = computed(
  () => currentChat.value?.status === wootConstants.STATUS_TYPE.RESOLVED
);

// ─── Funil: label da etapa atual ───
const FUNNEL_LABEL_NAMES = {
  'nao-contactado': 'Não Contactado',
  ia: 'IA',
  'contato-inicial': 'Contato Inicial',
  desenvolvimento: 'Desenvolvimento',
  negociacao: 'Negociação',
  'link-pagamento-gerado': 'Link de Pagamento',
  'venda-feita': 'Venda Feita',
  'nao-qualificado': 'Não Qualificado',
  perdido: 'Perdido',
  'sem-atendimento': 'Sem Atendimento',
};
const FUNNEL_ORDER = [
  'nao-contactado',
  'ia',
  'contato-inicial',
  'desenvolvimento',
  'negociacao',
  'link-pagamento-gerado',
  'venda-feita',
  'nao-qualificado',
  'perdido',
  'sem-atendimento',
];
const conversationLabels = computed(() => {
  const id = currentChat.value?.id;
  const fromStore = id
    ? store.getters['conversationLabels/getConversationLabels'](id)
    : null;
  if (Array.isArray(fromStore) && fromStore.length) return fromStore;
  return currentChat.value?.labels || [];
});
const funnelStageLabel = computed(() => {
  const labels = conversationLabels.value || [];
  const present = FUNNEL_ORDER.filter(
    s => labels.includes(s) && FUNNEL_LABEL_NAMES[s]
  );
  if (!present.length) return 'Funil';
  // Aceleres: 'sem-atendimento' é uma flag (conversa sem responsável), não uma
  // etapa real do funil. O backend Laravel (FunilLabelSync) a preserva como label
  // não-funil ao mover de etapa — então ela coexiste com 'negociacao', etc. Como
  // 'sem-atendimento' é a última em FUNNEL_ORDER, ela sobrescrevia a etapa real no
  // botão (bug: "em negociação aparecia Sem Atendimento"). Agora só retorna
  // 'Sem Atendimento' se for o ÚNICO label de funil presente.
  const realStages = present.filter(s => s !== 'sem-atendimento');
  const chosen = realStages.length ? realStages : present;
  return FUNNEL_LABEL_NAMES[chosen[chosen.length - 1]];
});

// ─── Transferir: lista de agentes ───
// Fonte: inboxAssignableAgents (agents atribuíveis do inbox da conversa) via
// useAgentsList — o mesmo store usado pelos pickers que funcionam (contextMenu,
// AgentSelector). O store global agents/getAgents vinha vazio nesta view,
// deixando o sub-menu "Transferir" sem agentes.
const { assignableAgents } = useAgentsList(false);
const transferAgents = computed(() => {
  const currentAssigneeId = currentChat.value?.meta?.assignee?.id;
  const currentUserId = currentUser.value?.id;
  return (assignableAgents.value || []).filter(
    a => a.id !== currentAssigneeId && a.id !== currentUserId
  );
});

// Busca + agrupamento por disponibilidade no sub-menu "Transferir"
const transferSearch = ref('');
const filteredTransferAgents = computed(() => {
  const q = transferSearch.value.trim().toLowerCase();
  const list = transferAgents.value || [];
  if (!q) return list;
  return list.filter(a => (a.name || '').toLowerCase().includes(q));
});
const onlineTransferAgents = computed(() =>
  filteredTransferAgents.value.filter(a => a.availability_status === 'online')
);
const offlineTransferAgents = computed(() =>
  filteredTransferAgents.value.filter(a => a.availability_status !== 'online')
);

const transferTo = agent => {
  toggleTransferMenu(false);
  toggleDropdown(false);
  store
    .dispatch('assignAgent', {
      conversationId: currentChat.value.id,
      agentId: agent.id,
    })
    .then(() => useAlert(`Conversa transferida para ${agent.name}`))
    .catch(() => useAlert('Falha ao transferir a conversa'));
};

// ─── Alteração de carteirização ───
const pendingOwnershipRequest = computed(
  () => ownershipStatus.value?.pending_request || null
);
const currentPortfolioOwner = computed(
  () => ownershipStatus.value?.current_owner || null
);
const canRequestOwnership = computed(
  () => !isAdmin.value && ownershipStatus.value?.can_request === true
);
const canManageOwnership = computed(
  () => isAdmin.value && ownershipStatus.value?.can_manage === true
);
const canOpenOwnership = computed(
  () => canManageOwnership.value || canRequestOwnership.value
);
const ownershipActionLabel = computed(() => {
  if (canManageOwnership.value) return 'Alterar carteirização';
  if (pendingOwnershipRequest.value)
    return 'Carteirização aguardando autorização';
  return 'Solicitar alteração de carteirização';
});
const ownershipAgents = computed(() => {
  return ownershipStatus.value?.eligible_targets || [];
});

const fetchOwnershipStatus = async () => {
  const conversationId = currentChat.value?.id;
  if (!conversationId) {
    ownershipStatus.value = null;
    return;
  }

  ownershipStatusLoading.value = true;
  try {
    const { data } =
      await ConversationTransferRequestsAPI.status(conversationId);
    if (Number(currentChat.value?.id) === Number(conversationId)) {
      ownershipStatus.value = data?.payload || null;
    }
  } catch {
    // Preserve the last known pending state during transient failures. Clearing
    // it here would incorrectly unlock the action while a request still exists.
  } finally {
    ownershipStatusLoading.value = false;
  }
};

const stopOwnershipStatusPolling = () => {
  if (!ownershipStatusPollId) return;

  window.clearInterval(ownershipStatusPollId);
  ownershipStatusPollId = null;
};

const refreshPendingOwnershipStatus = () => {
  if (!pendingOwnershipRequest.value) return;
  if (document.visibilityState !== 'visible') return;

  fetchOwnershipStatus();
};

const startOwnershipStatusPolling = () => {
  if (ownershipStatusPollId || !pendingOwnershipRequest.value) return;

  ownershipStatusPollId = window.setInterval(
    refreshPendingOwnershipStatus,
    10000
  );
};

const onOwnershipRequestRealtimeUpdate = async payload => {
  const currentAccountId = Number(store.getters.getCurrentAccountId);
  if (Number(payload?.account_id) !== currentAccountId) return;
  if (Number(payload?.conversation_id) !== Number(currentChat.value?.id)) {
    return;
  }

  await fetchOwnershipStatus();

  const isOwnRequest =
    Number(payload?.requester_id) === Number(currentUser.value?.id);
  const decidedByAnotherUser =
    Number(payload?.decided_by_id) !== Number(currentUser.value?.id);
  if (!isOwnRequest || !decidedByAnotherUser) return;

  if (payload?.status === 'approved') {
    useAlert('Sua alteração de carteirização foi aprovada');
  } else if (payload?.status === 'rejected') {
    useAlert('Sua alteração de carteirização foi rejeitada');
  }
};

const openOwnershipModal = async () => {
  toggleDropdown(false);
  await fetchOwnershipStatus();

  if (!ownershipAgents.value.length) {
    useAlert('Nenhum outro vendedor disponível para receber a carteirização');
    return;
  }
  showOwnershipModal.value = true;
};

const submitOwnershipRequest = async ({ targetAssigneeId, justification }) => {
  ownershipSubmitting.value = true;
  try {
    const { data } = canManageOwnership.value
      ? await ConversationTransferRequestsAPI.managerUpdate({
          conversationId: currentChat.value.id,
          targetAssigneeId,
        })
      : await ConversationTransferRequestsAPI.create({
          conversationId: currentChat.value.id,
          targetAssigneeId,
          justification,
        });
    showOwnershipModal.value = false;
    await fetchOwnershipStatus();
    if (canManageOwnership.value) {
      useAlert('Carteirização alterada com sucesso');
    } else {
      useAlert(
        data?.already_pending
          ? 'Já existe uma alteração de carteirização aguardando autorização'
          : 'Solicitação de alteração de carteirização enviada ao gestor'
      );
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível solicitar a alteração de carteirização'
    );
  } finally {
    ownershipSubmitting.value = false;
  }
};

// ─── Aurora IA: estado + toggle (usa endpoints do aceleres-aurora-btn.js) ───
const resolveAceleresApiBase = () => {
  const host = String(window.location.host || '').toLowerCase();
  if (host === 'atendimento.zoraxy.ai') return 'https://homolog.aceleres.ai';
  if (host === 'atendimento.aceleres.ai') return 'https://aceleres.ai';
  if (host.includes('zoraxy') || host.includes('homolog'))
    return 'https://homolog.aceleres.ai';
  return 'https://aceleres.ai';
};

const ACELERES_API = resolveAceleresApiBase();
let aclSessionPromise = null;
const auroraState = ref('off'); // 'off' | 'on' | 'unavailable' | 'loading' | 'pending'
const auroraReview = ref(null);

const readChatwootSessionInfo = () => {
  try {
    const match = document.cookie.match(/(?:^|;)\s*cw_d_session_info=([^;]*)/);
    if (!match) return null;
    const info = JSON.parse(decodeURIComponent(match[1]));
    if (!info?.['access-token'] || !info?.client || !info?.uid) return null;
    return info;
  } catch (_) {
    return null;
  }
};

const ensureAceleresSession = force => {
  if (!force && aclSessionPromise) return aclSessionPromise;

  aclSessionPromise = new Promise(resolve => {
    const info = readChatwootSessionInfo();
    if (!info) {
      resolve(false);
      return;
    }

    const body = new URLSearchParams();
    body.append('access_token', info['access-token'] || '');
    body.append('client', info.client || '');
    body.append('uid', info.uid || '');

    fetch(`${ACELERES_API}/api/kanban/zoraxy-login`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      body,
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => resolve(!!data?.success))
      .catch(() => resolve(false))
      .finally(() => {
        aclSessionPromise = null;
      });
  });

  return aclSessionPromise;
};

const fetchJsonWithSession = async (url, options = {}, retrying = false) => {
  const response = await fetch(url, options);
  const data = await response.json().catch(() => ({}));
  if (response.status !== 401 || retrying) return data;

  const ok = await ensureAceleresSession(true);
  if (!ok) return data;

  return fetchJsonWithSession(url, options, true);
};

const fetchAuroraStatus = async () => {
  const accId = store.getters.getCurrentAccountId;
  const convId = currentChat.value?.id;
  if (!accId || !convId) return;
  try {
    const data = await fetchJsonWithSession(
      `${ACELERES_API}/api/aurora/status?chatwoot_account_id=${accId}&chatwoot_conversation_id=${convId}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }
    );
    if (data?.disponivel === false) {
      auroraState.value = 'unavailable';
    } else if (data?.success && data?.pending) {
      auroraState.value = 'pending';
    } else if (data?.success && data?.ia_ativa_manual) {
      auroraState.value = 'on';
    } else {
      auroraState.value = 'off';
    }
    auroraReview.value = data?.pending_reviews?.[0] || null;
  } catch (_) {
    /* mantém estado anterior em falha */
  }
};

const reviewAuroraOutput = async () => {
  const review = auroraReview.value;
  if (!review) return;

  const reasons = (review.reasons || []).join('\n- ');
  // eslint-disable-next-line no-alert
  const confirmed = window.confirm(
    `A resposta automática foi bloqueada por risco potencial.\n\nMotivos:\n- ${reasons}\n\nPode haver falso positivo. Verifique o conteúdo com um superior antes de confirmar o envio.`
  );
  if (!confirmed) return;

  const data = await fetchJsonWithSession(`${ACELERES_API}/api/aurora/ativar`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      outbound_review_id: review.id,
      outbound_review_action: 'approve',
    }),
  });

  if (data?.success) {
    auroraReview.value = null;
    useAlert('Mensagem confirmada e enviada.');
    await fetchAuroraStatus();
  } else {
    useAlert(data?.message || 'Não foi possível confirmar a mensagem.');
  }
};

const toggleAurora = async () => {
  if (auroraState.value === 'unavailable' || auroraState.value === 'loading')
    return;
  const accId = store.getters.getCurrentAccountId;
  const convId = currentChat.value?.id;
  if (!accId || !convId) return;
  const wasOff = auroraState.value === 'off';
  auroraState.value = 'loading';
  const url = wasOff ? '/api/aurora/ativar' : '/api/aurora/desativar';
  try {
    const data = await fetchJsonWithSession(`${ACELERES_API}${url}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        chatwoot_account_id: parseInt(accId, 10),
        chatwoot_conversation_id: parseInt(convId, 10),
      }),
    });
    if (data?.success) {
      auroraState.value = wasOff ? 'on' : 'off';
      let successMessage = 'IA desativada — atendimento volta ao humano';
      if (wasOff) {
        auroraState.value = data.pending ? 'pending' : 'on';
        if (data.dispatched) {
          successMessage = 'IA ativada e respondendo!';
        } else if (data.pending) {
          successMessage = 'IA aguardando 10 segundos de segurança';
        } else {
          successMessage = 'IA ativada para próxima mensagem';
        }
      }
      useAlert(successMessage);
    } else {
      auroraState.value = wasOff ? 'off' : 'on';
      useAlert(`Erro: ${data?.message || 'falha ao alternar IA'}`);
    }
  } catch (_) {
    auroraState.value = wasOff ? 'off' : 'on';
    useAlert('Erro de conexão — tente novamente');
  }
};

onMounted(fetchAuroraStatus);

// Refetch quando muda de conversa
const lastFetchedConvId = ref(null);
const ensureAuroraStatusFresh = () => {
  const id = currentChat.value?.id;
  if (id && id !== lastFetchedConvId.value) {
    lastFetchedConvId.value = id;
    fetchAuroraStatus();
  }
};

// ─── Resolve / Mover etapa ───
const resolveConversation = () => {
  toggleDropdown(false);
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: wootConstants.STATUS_TYPE.RESOLVED,
    })
    .then(() => useAlert(t('CONVERSATION.CHANGE_STATUS')))
    .catch(() => useAlert('Falha ao concluir a conversa'));
};
const reopenConversation = () => {
  toggleDropdown(false);
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: wootConstants.STATUS_TYPE.OPEN,
    })
    .then(() => useAlert(t('CONVERSATION.CHANGE_STATUS')))
    .catch(() => useAlert('Falha ao reabrir a conversa'));
};
const openFunnelModal = () => {
  toggleDropdown(false);
  ensureMovementReasonPreferenceScript().then(() => {
    // eslint-disable-next-line no-underscore-dangle
    const openMoveModal = window.__aclOpenMoveModal;
    if (typeof openMoveModal === 'function') {
      openMoveModal();
    } else {
      useAlert('Seletor de etapas indisponível — recarregue a página.');
    }
  });
};

const openSnoozeNotificationMenu = () => {
  if (!isNotificationContext.value) return;
  toggleDropdown(false);
  document.querySelector('ninja-keys')?.open({ parent: 'snooze_notification' });
};

const snoozeNotification = async snoozedUntil => {
  if (!activeInboxNotification.value?.id) return;

  try {
    await store.dispatch('notifications/snooze', {
      id: activeInboxNotification.value.id,
      snoozedUntil,
    });
    useAlert(t('INBOX.ALERTS.SNOOZE'));
  } catch {
    // Keep the current conversation open when the notification update fails.
  }
};

const onCmdSnoozeNotification = snoozeType => {
  if (!isNotificationContext.value) return;

  if (snoozeType === wootConstants.SNOOZE_OPTIONS.UNTIL_CUSTOM_TIME) {
    showCustomSnoozeModal.value = true;
  } else if (typeof snoozeType === 'number') {
    snoozeNotification(snoozeType);
  } else {
    snoozeNotification(findSnoozeTime(snoozeType) || null);
  }
};

onMounted(() => {
  emitter.on(CMD_SNOOZE_NOTIFICATION, onCmdSnoozeNotification);
});

const scheduleCustomSnooze = customSnoozeTime => {
  showCustomSnoozeModal.value = false;
  if (customSnoozeTime) {
    snoozeNotification(getUnixTime(customSnoozeTime) || null);
  }
};

const deleteNotification = () => {
  if (!activeInboxNotification.value?.id) return;

  useTrack(INBOX_EVENTS.DELETE_NOTIFICATION);
  toggleDropdown(false);
  store
    .dispatch('notifications/delete', {
      notification: activeInboxNotification.value,
      unread_count: notificationMeta.value.unreadCount,
      count: notificationMeta.value.count,
    })
    .then(() => {
      useAlert(t('INBOX.ALERTS.DELETE'));
      router.replace({ name: 'inbox_view' });
    });
};

const onFunnelMoved = event => {
  const movedConversationId = Number(event?.detail?.conversationId);
  const selectedConversationId = Number(currentChat.value?.id);
  if (!movedConversationId || movedConversationId !== selectedConversationId) {
    return;
  }
  store.dispatch('conversationLabels/get', movedConversationId);
};

// ─── Menu items ───
const actionMenuItems = computed(() => {
  if (!isPersonalConversation.value) ensureAuroraStatusFresh();
  if (isTrainingChat.value) return [];

  const items = [];

  // Concluir / Reabrir
  if (isOpen.value) {
    items.push({
      icon: 'i-lucide-check-check',
      label: 'Concluir',
      action: 'resolve',
      value: 'resolve',
    });
  } else if (isResolved.value) {
    items.push({
      icon: 'i-lucide-rotate-ccw',
      label: 'Reabrir',
      action: 'reopen',
      value: 'reopen',
    });
  }

  // Mover de etapa
  if (!isPersonalConversation.value) {
    items.push({
      icon: 'i-lucide-filter',
      label: `Etapa: ${funnelStageLabel.value}`,
      action: 'move_stage',
      value: 'move_stage',
    });
  }

  // Transferir
  items.push({
    icon: 'i-lucide-user-plus',
    label: 'Transferir conversa',
    action: 'transfer',
    value: 'transfer',
  });

  if (canOpenOwnership.value) {
    items.push({
      icon: pendingOwnershipRequest.value
        ? 'i-lucide-clock-3'
        : 'i-lucide-user-round-cog',
      label: ownershipActionLabel.value,
      action: 'ownership_transfer',
      value: 'ownership_transfer',
      disabled:
        ownershipStatusLoading.value ||
        (!canManageOwnership.value && !!pendingOwnershipRequest.value),
    });
  }

  // Ativar / Desativar IA
  if (!isPersonalConversation.value && auroraState.value !== 'unavailable') {
    let label = 'Ativar IA';
    if (auroraState.value === 'on') {
      label = 'Desativar IA';
    } else if (auroraState.value === 'pending') {
      label = 'Cancelar ativação (10s)';
    } else if (auroraState.value === 'loading') {
      label = 'Aguarde…';
    }
    items.push({
      icon: 'i-lucide-bot',
      label,
      action: 'aurora_toggle',
      value: 'aurora_toggle',
      disabled: auroraState.value === 'loading',
    });
  }

  if (auroraReview.value) {
    items.push({
      icon: 'i-lucide-shield-alert',
      label: 'Revisar resposta bloqueada',
      action: 'aurora_review',
      value: 'aurora_review',
    });
  }

  // Mute / Unmute
  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  if (isNotificationContext.value) {
    items.push({
      icon: 'i-lucide-bell-minus',
      label: t('INBOX.ACTION_HEADER.SNOOZE'),
      action: 'snooze_notification',
      value: 'snooze_notification',
    });
    items.push({
      icon: 'i-lucide-trash-2',
      label: t('INBOX.ACTION_HEADER.DELETE'),
      action: 'delete',
      value: 'delete_notification',
    });
  }

  // Send transcript
  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  // Selecionar mensagens
  items.push({
    icon: 'i-lucide-check-square',
    label: 'Selecionar mensagens',
    action: 'select_messages',
    value: 'select_messages',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  if (isTrainingChat.value) {
    toggleDropdown(false);
    toggleTransferMenu(false);
    return;
  }

  if (action === 'resolve') {
    resolveConversation();
  } else if (action === 'reopen') {
    reopenConversation();
  } else if (action === 'move_stage') {
    openFunnelModal();
  } else if (action === 'transfer') {
    // Marca a abertura para que o clickaway (closeAll) ignore este clique.
    transferJustOpened = true;
    transferSearch.value = '';
    const inboxId = currentChat.value?.inbox_id;
    if (inboxId) {
      store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
    }
    toggleTransferMenu();
    setTimeout(() => {
      transferJustOpened = false;
    }, 0);
  } else if (action === 'ownership_transfer') {
    openOwnershipModal();
  } else if (action === 'snooze_notification') {
    openSnoozeNotificationMenu();
  } else if (action === 'delete' && isNotificationContext.value) {
    deleteNotification();
  } else if (action === 'aurora_toggle') {
    toggleAurora();
    toggleDropdown(false);
  } else if (action === 'aurora_review') {
    reviewAuroraOutput();
    toggleDropdown(false);
  } else if (action === 'mute') {
    toggleDropdown(false);
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    toggleDropdown(false);
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleDropdown(false);
    toggleEmailModal();
  } else if (action === 'select_messages') {
    toggleDropdown(false);
    enterSelectionMode();
  }
};

const closeAll = () => {
  // Ignora o clique que acabou de abrir o sub-menu "Transferir": o clickaway
  // dispara no mesmo evento (após o Vue desmontar o DropdownMenu) e fecharia
  // tudo. O guard transferJustOpened (limpo em setTimeout 0 / aqui) sinaliza
  // essa abertura.
  if (transferJustOpened) {
    transferJustOpened = false;
    return;
  }
  toggleDropdown(false);
  toggleTransferMenu(false);
};

// Comandos globais (Cmd-bar / Ninja keys)
const mute = () => {
  if (isTrainingChat.value) return;
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};
const unmute = () => {
  if (isTrainingChat.value) return;
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};
emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);
emitter.on(
  BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
  onOwnershipRequestRealtimeUpdate
);
emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, refreshPendingOwnershipStatus);
window.addEventListener('focus', refreshPendingOwnershipStatus);
document.addEventListener('visibilitychange', refreshPendingOwnershipStatus);
window.addEventListener('acl:funnel-moved', onFunnelMoved);

onUnmounted(() => {
  stopOwnershipStatusPolling();
  emitter.off(CMD_SNOOZE_NOTIFICATION, onCmdSnoozeNotification);
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
  emitter.off(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    onOwnershipRequestRealtimeUpdate
  );
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, refreshPendingOwnershipStatus);
  window.removeEventListener('focus', refreshPendingOwnershipStatus);
  document.removeEventListener(
    'visibilitychange',
    refreshPendingOwnershipStatus
  );
  window.removeEventListener('acl:funnel-moved', onFunnelMoved);
});

watch(pendingOwnershipRequest, pendingRequest => {
  if (pendingRequest) {
    startOwnershipStatusPolling();
  } else {
    stopOwnershipStatusPolling();
  }
});

watch(
  () => currentChat.value?.id,
  () => {
    showOwnershipModal.value = false;
    fetchOwnershipStatus();
  },
  { immediate: true }
);
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <div
      v-if="!isTrainingChat"
      v-on-clickaway="closeAll"
      class="relative flex items-center group"
    >
      <span class="acl-tip-wrapper" data-acl-tip="Mais opções">
        <ButtonV4
          icon="i-lucide-more-vertical"
          size="md"
          variant="ghost"
          color="slate"
          class="acl-conversation-more-actions-trigger rounded-md group-hover:bg-n-alpha-2"
          :aria-label="t('CONVERSATION.HEADER.MORE_ACTIONS')"
          @click.stop="toggleDropdown()"
        />
      </span>
      <DropdownMenu
        v-if="showActionsDropdown && !showTransferMenu"
        :menu-items="actionMenuItems"
        class="acl-conversation-more-actions-menu mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
      <!-- Sub-menu de transferência -->
      <Transition name="acl-transfer">
        <div
          v-if="showTransferMenu"
          class="acl-transfer-popup absolute z-50 mt-1 top-full ltr:right-0 rtl:left-0 w-[264px] max-h-80 flex flex-col rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg overflow-hidden"
        >
          <!-- Cabeçalho -->
          <div class="flex items-center gap-2 px-2.5 py-2">
            <button
              type="button"
              class="flex items-center justify-center size-6 -ml-0.5 rounded-md text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors cursor-pointer"
              :aria-label="transferCopy.backAria"
              @click="toggleTransferMenu(false)"
            >
              <span class="i-lucide-chevron-left size-4" />
            </button>
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ transferCopy.title }}
              </span>
              <span class="text-[11px] leading-tight text-n-slate-11 truncate">
                {{ transferCopy.subtitle }}
              </span>
            </div>
          </div>

          <!-- Busca -->
          <div class="px-2.5 pb-2">
            <div class="relative">
              <span
                class="absolute i-lucide-search size-3.5 top-2 left-2.5 text-n-slate-10"
              />
              <input
                v-model="transferSearch"
                type="search"
                :placeholder="transferCopy.searchPlaceholder"
                class="reset-base w-full h-8 py-2 pl-9 pr-2 text-sm border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
              />
            </div>
          </div>

          <!-- Lista -->
          <div class="flex-1 overflow-y-auto px-2 pb-2">
            <template v-if="onlineTransferAgents.length">
              <p
                class="px-2 pt-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-11"
              >
                {{ transferCopy.availableGroup }}
              </p>
              <button
                v-for="agent in onlineTransferAgents"
                :key="'on-' + agent.id"
                type="button"
                class="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-all duration-200 ease-in-out hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
                @click="transferTo(agent)"
              >
                <Avatar
                  :name="agent.name"
                  :src="agent.thumbnail"
                  :status="agent.availability_status"
                  :size="26"
                  rounded-full
                />
                <div class="flex flex-col min-w-0">
                  <span class="text-sm text-n-slate-12 truncate">{{
                    agent.name
                  }}</span>
                  <span
                    class="text-[11px] leading-tight text-n-teal-10 truncate"
                  >
                    {{ transferCopy.availableStatus }}
                  </span>
                </div>
              </button>
            </template>

            <template v-if="offlineTransferAgents.length">
              <p
                class="px-2 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-11"
              >
                {{ transferCopy.offlineGroup }}
              </p>
              <button
                v-for="agent in offlineTransferAgents"
                :key="'off-' + agent.id"
                type="button"
                class="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-all duration-200 ease-in-out hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
                @click="transferTo(agent)"
              >
                <Avatar
                  :name="agent.name"
                  :src="agent.thumbnail"
                  :status="agent.availability_status"
                  :size="26"
                  rounded-full
                />
                <div class="flex flex-col min-w-0">
                  <span class="text-sm text-n-slate-12 truncate">{{
                    agent.name
                  }}</span>
                  <span
                    class="text-[11px] leading-tight text-n-slate-10 truncate"
                  >
                    {{ transferCopy.offlineStatus }}
                  </span>
                </div>
              </button>
            </template>

            <p
              v-if="
                !onlineTransferAgents.length && !offlineTransferAgents.length
              "
              class="px-2 py-4 text-center text-xs text-n-slate-11"
            >
              {{ transferCopy.emptyState }}
            </p>
          </div>
        </div>
      </Transition>
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <AceleresOwnershipTransferModal
      :show="showOwnershipModal"
      :current-owner="currentPortfolioOwner"
      :agents="ownershipAgents"
      :manager-mode="canManageOwnership"
      :submitting="ownershipSubmitting"
      @close="showOwnershipModal = false"
      @submit="submitOwnershipRequest"
    />
    <woot-modal
      v-model:show="showCustomSnoozeModal"
      :on-close="() => (showCustomSnoozeModal = false)"
    >
      <CustomSnoozeModal
        @close="showCustomSnoozeModal = false"
        @choose-time="scheduleCustomSnooze"
      />
    </woot-modal>
  </div>
</template>

<style scoped>
/* Tooltip estilo sidebar (#aceleres-topbar .acl-tb-btn::before) */
.acl-tip-wrapper {
  position: relative;
  display: inline-flex;
}
.acl-tip-wrapper::after {
  content: attr(data-acl-tip);
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #0f172a;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s;
  z-index: 1000;
}
.acl-tip-wrapper:hover::after {
  opacity: 1;
}

.acl-conversation-more-actions-trigger,
.acl-conversation-more-actions-trigger :deep(span),
.acl-conversation-more-actions-trigger :deep([class*='i-']) {
  color: #111827 !important;
}

.acl-conversation-more-actions-trigger {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  padding: 0 !important;
}

.acl-conversation-more-actions-trigger :deep([class*='i-']) {
  display: inline-flex !important;
  width: 18px !important;
  height: 18px !important;
  flex: 0 0 18px;
  background-color: currentColor !important;
  font-size: 18px !important;
  opacity: 1 !important;
}

.acl-conversation-more-actions-menu {
  z-index: 1000;
  min-width: 300px !important;
}

:deep(.acl-conversation-more-actions-menu button) {
  width: 100%;
  height: 3.5rem !important;
  min-height: 3.5rem !important;
  gap: 0.85rem !important;
  padding-inline: 0.9rem !important;
  justify-content: flex-start;
  color: #111827 !important;
}

:deep(.acl-conversation-more-actions-menu button.text-n-ruby-11) {
  color: #be123c !important;
}

:deep(.acl-conversation-more-actions-menu button [class*='i-']) {
  display: inline-flex !important;
  width: 28px !important;
  height: 28px !important;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  color: inherit !important;
  background-color: currentColor !important;
  font-size: 28px !important;
  opacity: 1 !important;
}

/* Aceleres: rotulo do menu maior que o padrao text-sm do DropdownMenu
   compartilhado - so aqui, via :deep, sem mexer no componente base pra
   nao afetar os outros menus que usam o mesmo DropdownMenu. */
:deep(.acl-conversation-more-actions-menu button span) {
  font-size: 1.15rem !important;
  font-weight: 500;
}

/* Sub-menu Transferir — animação de entrada/saída (origem canto superior) */
.acl-transfer-enter-active,
.acl-transfer-leave-active {
  transition:
    opacity 0.14s ease,
    transform 0.14s ease;
}
.acl-transfer-enter-from,
.acl-transfer-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(-2px);
}
</style>
